import { useQueryClient } from '@tanstack/react-query';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { setTenantProvider, setTenantRejectedHandler } from '../api/http';
import { queryKeys } from '../api/queryKeys';
import { useCallerContextQuery, useIsAdministrator } from '../auth/useCallerContext';
import { TenantChooser } from './TenantChooser';
import {
  TenantSelectionContext,
  initialSelection,
  readStoredTenant,
  storeTenant,
  type TenantSelectionValue,
} from './tenantSelection';

/**
 * Owns the globally selected tenant. Sits below TenantGate so the caller context is already
 * loaded, and installs the request header during render — the pages below fire their queries on
 * first mount, and every one of them must already carry the selection.
 */
export function TenantProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const isAdministrator = useIsAdministrator();
  const query = useCallerContextQuery();

  // An administrator heading to /admin gets past TenantGate even when this query failed; the
  // provider then simply has nobody to offer and nothing selected.
  const tenants = useMemo(() => query.data?.tenants ?? [], [query.data]);

  const [selectedTenantGid, setSelectedTenantGid] = useState<string | null>(() =>
    initialSelection(readStoredTenant(), tenants, isAdministrator),
  );

  // The header reads a ref rather than the state so the ambient provider installed once keeps
  // seeing the current value without re-registration.
  const selectedRef = useRef(selectedTenantGid);
  selectedRef.current = selectedTenantGid;
  setTenantProvider(() => selectedRef.current);

  const tenantScopedFamilies = useMemo(
    () => [
      queryKeys.accounts.all,
      queryKeys.files.all,
      queryKeys.operations.all,
      queryKeys.mappings.all,
      queryKeys.codelists.all,
      queryKeys.inbound.all,
      queryKeys.flowis.all,
    ],
    [],
  );

  const dropTenantCaches = useCallback(() => {
    for (const family of tenantScopedFamilies) {
      void queryClient.cancelQueries({ queryKey: family });
      queryClient.removeQueries({ queryKey: family });
    }
  }, [queryClient, tenantScopedFamilies]);

  const clearSelection = useCallback(() => {
    dropTenantCaches();
    storeTenant(null);
    setSelectedTenantGid(null);
  }, [dropTenantCaches]);

  const selectTenant = useCallback(
    (tenantGid: string) => {
      // In-flight answers for the old tenant are aborted and its cache dropped: a switch means
      // "start clean in the other tenant", not "keep what the last one already showed".
      dropTenantCaches();
      storeTenant(tenantGid);
      setSelectedTenantGid(tenantGid);
    },
    [dropTenantCaches],
  );

  // The server refusing the named tenant means the stored choice went stale (revoked, disabled).
  // Dropping it re-opens the chooser; refreshing the caller context refreshes what it offers.
  useEffect(() => {
    setTenantRejectedHandler(() => {
      // A missing-selection refusal can arrive after the choice is already empty. Dropping caches in
      // that state would remount the same queries and turn the refusal into a request loop.
      if (selectedRef.current === null) return;

      clearSelection();
      void queryClient.invalidateQueries({ queryKey: queryKeys.caller.all });
    });

    return () => setTenantRejectedHandler(() => {});
  }, [clearSelection, queryClient]);

  // Reconcile whenever a fresh caller context arrives. This restores a stored choice after the
  // administrator-only error path recovers, and mirrors the server's single-tenant auto-selection
  // when revocation leaves a non-administrator with exactly one choice.
  useEffect(() => {
    if (query.data === undefined) return;

    const current = selectedRef.current;
    if (current !== null && tenants.some((tenant) => tenant.tenantGid === current)) return;

    const next = initialSelection(current ?? readStoredTenant(), tenants, isAdministrator);
    if (next === current) return;

    if (next === null) clearSelection();
    else selectTenant(next);
  }, [query.data, tenants, isAdministrator, clearSelection, selectTenant]);

  // Only somebody who genuinely has to pick before anything makes sense — several tenants, no
  // administrator role — is stopped by the full-page chooser. An administrator is never blocked:
  // they land in administration, and the tenant-dependent sections simply wait (disabled in the
  // nav) until a tenant is picked in the toolbar.
  const mustChoose = selectedTenantGid === null && !isAdministrator && tenants.length > 1;

  const value = useMemo<TenantSelectionValue>(
    () => ({
      tenants,
      selectedTenantGid,
      selectedTenant: tenants.find((tenant) => tenant.tenantGid === selectedTenantGid) ?? null,
      selectTenant,
      clearSelection,
    }),
    [tenants, selectedTenantGid, selectTenant, clearSelection],
  );

  return (
    <TenantSelectionContext.Provider value={value}>
      {mustChoose ? (
        <TenantChooser />
      ) : (
        <Fragment key={selectedTenantGid ?? 'none'}>{children}</Fragment>
      )}
    </TenantSelectionContext.Provider>
  );
}
